/**
 * Website icons in the vault list (Standalone vault plan, Part 2 "Site icons").
 *
 * Privacy rules, all enforced here:
 *  - the key is the registrable domain from the shared core
 *    (`vault_site_keys`), never a URL, a path or a username;
 *  - icons are static files at `<base>/<domain>` (base from GET /icons, the
 *    object store's public URL): no cookie, no token, no referrer;
 *  - a missing icon is reported ONCE per domain per 30 days per device, after
 *    a random delay (never a burst at unlock), anonymously;
 *  - the setting off ("Show website icons") means zero icon requests.
 */

const API_BASE = (process.env.REACT_APP_API_BASE_URL || 'https://api.apiboo.com/api/v1').replace(/\/+$/, '');
const ENABLED_KEY = 'apiboo.vault.siteIcons';
const BASE_KEY = 'apiboo.vault.siteIconBase';
const ASKED_KEY = 'apiboo.vault.siteIconAsked';
const BASE_TTL_MS = 24 * 60 * 60 * 1000;
const ASK_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const ASK_MAX_DELAY_MS = 30 * 1000;
const ASKED_MAX = 2000;
export const SITE_ICONS_CHANGED = 'vault-site-icons-changed';

function readJson(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // a full or blocked storage only costs a repeat request later
  }
}

/** On by default (the plan's choice); off means no icon request of any kind. */
export function siteIconsEnabled() {
  return readJson(ENABLED_KEY, true) !== false;
}

export function setSiteIconsEnabled(on) {
  writeJson(ENABLED_KEY, !!on);
  window.dispatchEvent(new CustomEvent(SITE_ICONS_CHANGED, { detail: { enabled: !!on } }));
}

let basePromise = null;
/**
 * A failed lookup (offline, a 5xx, a 429) is remembered for a while. Without
 * this every vault row asked `/icons` again on every render — 349 rows made
 * 349 requests, the API answered "Too many requests" to this machine for
 * everyone, and no client here showed an icon (owner report 11.10.2026).
 */
const BASE_RETRY_MS = 5 * 60 * 1000;
let baseFailedAt = 0;

/** Where icons live, or null (storage off on the server, offline). Cached a day. */
export function iconBase({ fetchImpl = window.fetch.bind(window), now = Date.now } = {}) {
  const cached = readJson(BASE_KEY, null);
  if (cached && typeof cached.base === 'string' && now() - cached.at < BASE_TTL_MS) {
    return Promise.resolve(cached.base);
  }
  if (baseFailedAt && now() - baseFailedAt < BASE_RETRY_MS) return Promise.resolve(null);
  if (!basePromise) {
    basePromise = fetchImpl(`${API_BASE}/icons`, { credentials: 'omit', referrerPolicy: 'no-referrer' })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        const base = body && typeof body.base === 'string' && /^https:\/\//.test(body.base) ? body.base : null;
        if (base) {
          writeJson(BASE_KEY, { base, at: now() });
          baseFailedAt = 0;
        } else {
          baseFailedAt = now();
        }
        return base;
      })
      .catch(() => {
        baseFailedAt = now();
        return null;
      })
      .finally(() => {
        basePromise = null;
      });
  }
  return basePromise;
}

/** Tests only: forget a remembered failure. */
export function resetIconBaseForTests() {
  baseFailedAt = 0;
  basePromise = null;
}

export function iconUrl(base, siteKey) {
  return `${base.replace(/\/+$/, '')}/${encodeURIComponent(siteKey)}`;
}

/**
 * The icon file was missing: ask the server to fetch it — at most once per
 * domain per 30 days on this device, after a random delay. Returns whether a
 * request was scheduled.
 */
export function reportMissing(
  siteKey,
  { fetchImpl = window.fetch.bind(window), now = Date.now, delayMs = Math.random() * ASK_MAX_DELAY_MS, schedule = setTimeout } = {}
) {
  if (!siteIconsEnabled() || typeof siteKey !== 'string' || !siteKey.includes('.')) return false;
  const asked = readJson(ASKED_KEY, {});
  const last = asked[siteKey];
  if (typeof last === 'number' && now() - last < ASK_TTL_MS) return false;
  asked[siteKey] = now();
  const keys = Object.keys(asked);
  if (keys.length > ASKED_MAX) {
    keys.sort((a, b) => asked[a] - asked[b]).slice(0, keys.length - ASKED_MAX).forEach((k) => delete asked[k]);
  }
  writeJson(ASKED_KEY, asked);
  schedule(() => {
    if (!siteIconsEnabled()) return;
    fetchImpl(`${API_BASE}/icons/request`, {
      method: 'POST',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ domain: siteKey }),
    }).catch(() => {});
  }, delayMs);
  return true;
}
